import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Smartphone, QrCode, Rocket, MapPin, Camera, BellRing, Ticket, ScanLine, Download, Store, Apple, Play } from "lucide-react";
import Seo from "@/components/Seo";
import { useLanguage } from "@/i18n/LanguageContext";

const STEPS = [
  {
    icon: Download,
    title: "Installe Expo Go",
    text: "Télécharge l'application gratuite Expo Go sur l'App Store (iPhone) ou Google Play (Android).",
    links: true,
  },
  {
    icon: QrCode,
    title: "Scanne le code",
    text: "Ouvre Expo Go et touche « Scan QR Code » (ou utilise la caméra de ton iPhone, qui détecte Expo Go automatiquement).",
  },
  {
    icon: Rocket,
    title: "Profite de GoSpot",
    text: "L'app se charge avec les vraies données : tes comptes, favoris, réservations et ton Espace Pro.",
  },
];

const FEATURES = [
  { icon: MapPin, label: "Carte plein écran", desc: "avec ta position en direct" },
  { icon: Camera, label: "Photos Pro", desc: "caméra et galerie natives" },
  { icon: ScanLine, label: "Scanner de billets", desc: "validation QR à l'entrée" },
  { icon: BellRing, label: "Notifications push", desc: "réponses admin en temps réel" },
  { icon: Ticket, label: "Billets QR", desc: "dans ta poche, hors ligne" },
  { icon: Store, label: "Espace Pro", desc: "gestion complète au pouce" },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

export default function MobilePage() {
  const { t } = useLanguage();
  return (
    <main data-testid="mobile-page" className="bg-[#F8FAFC] pb-20">
      <Seo
        title={t("Application mobile GoSpot — iOS & Android")}
        siteName="GoSpot"
        description={t("Télécharge l'app GoSpot sur ton téléphone : carte plein écran, billets QR, notifications et Espace Pro. Scanne le code pour l'installer avec Expo Go.")}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "GoSpot",
          applicationCategory: "LifestyleApplication",
          operatingSystem: "iOS, Android",
          offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
        }}
      />

      {/* HERO */}
      <section className="mx-auto max-w-6xl px-4 pt-10 sm:px-6 sm:pt-14">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6FF] px-3.5 py-1.5 text-xs font-bold text-[#2563EB]">
              <Smartphone className="h-3.5 w-3.5" />
              iOS & Android
            </span>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              {t("GoSpot dans ta poche")}
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-500">
              {t("L'application mobile GoSpot : carte plein écran, géolocalisation, billets QR, notifications et ton Espace Pro — synchronisée avec ton compte web.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              <span className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700">
                <Apple className="h-4 w-4" /> iPhone
              </span>
              <span className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700">
                <Play className="h-4 w-4" /> Android
              </span>
            </div>
          </motion.div>

          {/* CARTE QR */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mx-auto w-full max-w-sm">
            <div data-testid="qr-card" className="rounded-[2rem] border border-slate-100 bg-white p-8 text-center shadow-[0_12px_40px_rgba(37,99,235,0.10)]">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("Scanne pour ouvrir l'app")}</p>
              <div className="mx-auto mt-5 w-fit rounded-3xl border-4 border-[#2563EB]/10 bg-white p-3">
                <img src="/expo-qr.png" alt={t("Code QR Expo Go de l'application mobile GoSpot")} className="h-56 w-56 rounded-xl" data-testid="expo-qr-image" />
              </div>
              <p className="mt-5 text-sm font-bold text-slate-900">GoSpot Mobile</p>
              <p className="mt-1 text-xs text-slate-400">{t("Via Expo Go — gratuit, rien à créer")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ÉTAPES */}
      <section className="mx-auto mt-14 max-w-6xl px-4 sm:px-6">
        <h2 className="text-center text-xl font-extrabold text-slate-900">{t("Installation en 3 étapes")}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 * i }}
              data-testid={`step-${i + 1}`}
              className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#EFF6FF]">
                  <step.icon className="h-5 w-5 text-[#2563EB]" />
                </span>
                <span className="text-2xl font-extrabold text-slate-200">{i + 1}</span>
              </div>
              <h3 className="mt-4 text-base font-bold text-slate-900">{t(step.title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{t(step.text)}</p>
              {step.links && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href="https://apps.apple.com/app/expo-go/id982107779"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="expo-go-ios-link"
                    className="flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-700"
                  >
                    <Apple className="h-3.5 w-3.5" /> App Store
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=host.exp.exponent"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="expo-go-android-link"
                    className="flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-700"
                  >
                    <Play className="h-3.5 w-3.5" /> Google Play
                  </a>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* FONCTIONNALITÉS */}
      <section className="mx-auto mt-14 max-w-6xl px-4 sm:px-6">
        <h2 className="text-center text-xl font-extrabold text-slate-900">{t("Pensée pour le téléphone")}</h2>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.05 * i }}
              className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EFF6FF]">
                <f.icon className="h-4.5 w-4.5 text-[#2563EB]" size={18} />
              </span>
              <p className="mt-3 text-sm font-bold text-slate-900">{t(f.label)}</p>
              <p className="mt-0.5 text-xs text-slate-400">{t(f.desc)}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-10 text-center text-xs text-slate-400">
          {t("Tu préfères le navigateur ?")} <Link to="/" className="font-semibold text-[#2563EB] hover:underline">{t("Retour au site GoSpot")}</Link>
        </p>
      </section>
    </main>
  );
}
